import { useState, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../services/api';

const decodeJwtPayload = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
};

const ProtectedRoute = ({ allowedRoles, allowedFeatures }) => {
  const { session, role, availableFeatures, loading: sessionLoading, updateVerifiedRole, terminateSession } = useAuth();
  const [verifying, setVerifying] = useState(true);

  useEffect(() => {
    if (sessionLoading || !session) {
      setVerifying(false);
      return;
    }

    let cancelled = false;

    API
      .get('/get-role/', {
        withCredentials: true,
      })
      .then((res) => {
        if (cancelled) return;

        const responseRole = res.data?.role || null;
        const decoded = decodeJwtPayload(localStorage.getItem('token') || session.access_token);
        const jwtRole = decoded?.app_metadata?.app_role || null;

        if (responseRole !== jwtRole) {
          terminateSession();
          return;
        }

        updateVerifiedRole(responseRole, res.data?.availableFeatures || []);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[ProtectedRoute] Role verification failed:', err);
          updateVerifiedRole(null, []);
        }
      })
      .finally(() => {
        if (!cancelled) setVerifying(false);
      });

    return () => {
      cancelled = true;
    };
  }, [session, sessionLoading, updateVerifiedRole, terminateSession]);

  if (sessionLoading || verifying) return null;

  if (!session) return <Navigate to="/" replace />;

  // role is now sourced from JWT-verified cross-check, not raw HTTP response
  const isAuthorizedRole =
    typeof allowedRoles === 'string'
      ? role === allowedRoles
      : Array.isArray(allowedRoles) && allowedRoles.includes(role);

  const isAuthorizedFeature =
    typeof allowedFeatures === 'string'
      ? availableFeatures.includes(allowedFeatures)
      : Array.isArray(allowedFeatures)
      ? allowedFeatures.every((f) => availableFeatures.includes(f))
      : true;

  if (isAuthorizedRole && isAuthorizedFeature) return <Outlet />;

  return <Navigate to="/404" replace />;
};

export default ProtectedRoute;
